import { useContext } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useGet } from 'hooks';
import styled from 'styled-components';
import Filter from '../Purchasing/helpers/FilterField';
import {
  performPurchasingSearch,
  clearPurchasingFilters,
  setPurchasingFilter,
} from 'actions/organisation';
import { Button, SearchButton } from 'components/common';
import {
  purchasers,
  purchaserStates,
  purchaserCountries,
} from 'services/inputTokens';
import { OrgPurchasingSearchContext } from '..';
import { purchasingCategoriesRemote } from 'services/inputTokens';
import { Token } from 'components/common/TokenInput';

const Content = styled.div`
  padding: 0px 0px 10px;
  > * {
    margin-bottom: 10px;
    :last-child {
      margin-bottom: 5px;
    }
  }
`;

export const Actions = styled.div`
  padding-top: 10px;
  display: flex;
  justify-content: flex-end;
  position: sticky;
  bottom: 0;
  padding-bottom: 10px;
  background: white;
`;

const OrganisationPurchasingFilters = ({
  organisation,
  loading,
  handleCancel,
}) => {
  const postSearch = useContext(OrgPurchasingSearchContext);
  const dispatch = useDispatch();
  const [{ res: countriesResponse, loading: countriesLoading }] = useGet({
    url: purchaserCountries,
    onMount: true,
  });
  const [
    { res: purchasingTypesResponse, loading: purchasingTypesLoading },
  ] = useGet({
    url: purchasingCategoriesRemote,
    onMount: true,
  });

  const handleSearch = () => {
    dispatch(
      performPurchasingSearch(postSearch, {
        organisationId: organisation.id,
        newSearch: true,
      })
    );
  };

  const handleClear = () => {
    dispatch(clearPurchasingFilters);
    dispatch(
      setPurchasingFilter('supplierName', [organisation.organisationName])
    );
    handleSearch();
  };
  return (
    <>
      <Content>
        <Filter
          filterType='selectIncludeExclude'
          filterId='purchaserType'
          placeholder='Purchaser Type'
          options={
            purchasingTypesLoading
              ? []
              : purchasingTypesResponse.map(o => ({
                  label: o.categoryName,
                  value: o.categoryName,
                }))
          }
          mode={'multiple'}
          style={{ width: '100%' }}
          tagRender={tagRender}
          disabled={loading}
        />
        <Filter
          filterType='selectIncludeExclude'
          filterId='purchaserName'
          placeholder='Purchaser Name'
          options={purchasers}
          disabled={loading}
          tagRender={tagRender}
          style={{ width: '100%' }}
          mode={'multiple'}
        />
        <Filter
          allowClear={false}
          filterType='selectIncludeExclude'
          filterId='purchaserCountry'
          placeholder='Purchaser Country'
          options={
            countriesLoading
              ? []
              : countriesResponse.map(o => ({
                  label: o.countryName,
                  value: o.countryName,
                }))
          }
          mode={'multiple'}
          style={{ width: '100%' }}
          tagRender={tagRender}
          disabled={loading}
        />
        <Filter
          filterType='selectIncludeExclude'
          mode={'multiple'}
          style={{ width: '100%' }}
          tagRender={tagRender}
          placeholder='Purchaser State / Province'
          filterId='purchaserState'
          options={purchaserStates}
          optionProp={{
            optionLabel: 'displayName',
            optionValue: 'id',
          }}
          remoteSearchOnClick
          disabled={loading}
        />
        <Filter
          filterType='token'
          placeholder='Purchaser Zip / Postal Code'
          filterId='purchaserZip'
          disabled={loading}
          includeExclude
        />
        <Filter
          filterType='token'
          filterId='item'
          placeholder='Item'
          disabled={loading}
          includeExclude
        />
        <Filter
          filterType='datePicker'
          filterId='date'
          type='range'
          placeholder={['Purchase Date From', 'Purchase Date To']}
          disabled={loading}
        />
        <Filter
          filterType='number'
          filterId='minPrice'
          min={0}
          placeholder={'Min. Price (USD)'}
          disabled={loading}
        />
        <Filter
          filterType='number'
          filterId='maxPrice'
          min={0}
          placeholder={'Max. Price (USD)'}
          disabled={loading}
        />
      </Content>
      <Actions>
        <Button
          data-testid='filter-reset'
          onClick={handleClear}
          disabled={loading}
        >
          Reset
        </Button>
        <SearchButton
          type='primary'
          style={{ marginLeft: '10px' }}
          data-testid='filter-update'
          loading={loading}
          disabled={loading}
          onSearch={handleSearch}
          onCancel={handleCancel}
          location='OrganisationPurchasingSearch'
          searchText='Update'
        />
      </Actions>
    </>
  );
};

const tagRender = props => {
  const { label, onClose } = props;
  const onPreventMouseDown = event => {
    event.preventDefault();
    event.stopPropagation();
  };
  return (
    <Token
      key={label}
      value={label}
      removeToken={onClose}
      style={{ marginTop: 'auto', marginBottom: 'auto' }}
      onMouseDown={onPreventMouseDown}
    />
  );
};

OrganisationPurchasingFilters.propTypes = {
  organisations: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }),
  loading: PropTypes.bool,
};

export default OrganisationPurchasingFilters;
